import {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from '@/stores/useToastStore'
import { AgGridReact } from 'ag-grid-react'
import {
  AllCommunityModule,
  ModuleRegistry,
  type RowDragEndEvent,
  type ColumnMovedEvent,
  type ColumnResizedEvent,
} from 'ag-grid-community'
import { Loader2, Lock } from 'lucide-react'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { ProductsTableToolbar } from './ProductsTableToolbar'
import { useColumnVisibilityStore } from '@/stores/useColumnVisibilityStore'
import {
  freezeScopeKey,
  useColumnFreezeStore,
  usePinnedColumnIds,
} from '@/stores/useColumnFreezeStore'
import { useActiveColumnViewStore } from '@/stores/useActiveColumnViewStore'
import { FreezeColumnDivider } from './FreezeColumnDivider'
import { useColumnOrder, useUpdateColumnOrder } from '@/hooks/useColumnOrder'
import { useColumnWidths, useUpdateColumnWidth } from '@/hooks/useColumnWidths'
import { useColumnViews } from '@/hooks/useColumnViews'
import { useElementHeight } from '@/hooks/useElementHeight'
import { BulkActionsBar } from './BulkActionsBar'
import { ProductsTablePagination } from './ProductsTablePagination'
import { ProductsTableDialogs } from './ProductsTableDialogs'
import {
  NameCell,
  OptionRowCell,
  SkuCell,
  LineActionsCell,
} from './cellRenderers'
import { DagCellRenderer } from './DagCellRenderer'
import {
  buildDagColumnDefs,
  flattenColumnIds,
  BULK_EDIT_COLUMN_ID,
  NON_REORDERABLE_COLUMN_IDS,
} from './buildDagColumnDefs'
import type { ColumnTotal } from './DagColumnHeaderWithTotals'
import {
  DagColumnHeaderLabel,
  type DagColumnHeaderLabelProps,
} from './DagColumnHeaderLabel'
import { useDagTabNavigation } from './useDagTabNavigation'
import { useDagDropdownPaste } from './useDagDropdownPaste'
import { useDagTableStore } from '@/stores/useDagTableStore'
import { useColumnTotalsStore } from '@/stores/useColumnTotalsStore'
import { AssignStoreModelDrawer } from './AssignStoreModelDrawer'
import { PlanWavesDrawer } from './PlanWavesDrawer'
import { MultilocDrawer } from './MultilocDrawer'
import { SecondaryAttributesDrawer } from './SecondaryAttributesDrawer'
import { ColumnsConfigurationDrawer } from './ColumnsConfigurationDrawer'
import { AssignParentSkuDrawer } from './AssignParentSkuDrawer'
import { ManagePIABDrawer } from './ManagePIABDrawer'
import { RetailPricingDrawer } from './RetailPricingDrawer'
import { BulkColumnEditModal } from './BulkColumnEditModal'
import { ForecastModelDrawer } from './ForecastModelDrawer'
import { ManageLineImagesModal } from './ManageLineImagesModal'
import { ImageCarouselModal } from './ImageCarouselModal'
import { LineItemCommentsDrawer } from './LineItemCommentsDrawer'
import { ThumbnailTracker } from './ThumbnailTracker'
import {
  useProductsTableState,
  dagRowToRowData,
  rowDataToPlanDetailItem,
  type RowData,
} from './useProductsTableState'
import { useProductsTableMutations } from './useProductsTableMutations'
import {
  useDagMetadata,
  useDagRows,
  useDropdownLookups,
  useReorderDagRow,
  getDagRowsQueryKey,
} from '@/hooks/useDagTable'
import { useUpdatePlanHeader } from '@/hooks/useAssortmentPlans'
import { useHierarchyForDag } from '@/hooks/useHierarchyForDag'
import { useDagSSE } from '@/hooks/useDagSSE'
import { useStableCallback } from '@/hooks/useStableCallback'
import { useStuckComputingWatcher } from '@/hooks/useStuckComputingWatcher'
import { useColumnVisibility } from '@/hooks/useColumnVisibility'
import { MOCK_OPTIONS } from '@/mocks/productOptions'
import { queryKeys } from '@/lib/queryKeys'
import { findDagRowIndexByLineRef } from '@/api/dagTable'
import { getErrorMessage } from '@/utils/getErrorMessage'
import type { PlanDetailItem, ApiPlanStatus, FilterGroup } from '@/types'
import type { DagRowRead, DagTableLoadResponse } from '@/types/dag'
import { getConfigurableColumns } from './columnConfiguration'
import { isFilterableColumn } from './dagFilterUtils'
import { computeReorderPlan } from './rowClusters'

// Register AG Grid modules
ModuleRegistry.registerModules([AllCommunityModule])

interface ProductsTableProps {
  planId: string
  versionId: string | undefined
  page: number
  pageSize: number
  onPageChange: (page: number) => void
  onPageSizeChange: (pageSize: number) => void
  onAddItem?: () => void
  onImport?: () => void
  onExport?: () => void
  isExporting?: boolean
  onBulkDeleteSuccess?: () => void
  isReadOnly?: boolean
  planStatus?: ApiPlanStatus
  primaryAttributes?: string[]
  onReplaceDummySkus?: (lineItemIds: string[]) => void
}

export function ProductsTable({
  planId,
  versionId,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onAddItem,
  onImport,
  onExport,
  isExporting,
  onBulkDeleteSuccess,
  isReadOnly,
  planStatus,
  primaryAttributes = [],
  onReplaceDummySkus,
}: ProductsTableProps) {
  const { ref: toolbarRef, height: toolbarHeight } =
    useElementHeight<HTMLDivElement>()
  const queryClient = useQueryClient()

  // ---- Search state (debounced, sent to backend) --------------------------
  const [searchQuery, setSearchQuery] = useState('')
  const prevSearchRef = useRef(searchQuery)

  useEffect(() => {
    if (prevSearchRef.current !== searchQuery) {
      prevSearchRef.current = searchQuery
      onPageChange(1)
    }
  }, [searchQuery, onPageChange])

  const handleSearch = useCallback((debouncedValue: string) => {
    setSearchQuery(debouncedValue)
  }, [])

  // ---- Advanced filter state (AND/OR rule tree) -----------------------------
  const [filterGroup, setFilterGroup] = useState<FilterGroup | null>(null)

  const handleApplyFilter = useCallback(
    (next: FilterGroup | null) => {
      setFilterGroup(next)
      onPageChange(1)
    },
    [onPageChange]
  )

  // ---- Data fetching -------------------------------------------------------
  const { data: metadata } = useDagMetadata(planId, versionId)
  const {
    data: rowsData,
    isLoading,
    isFetching,
  } = useDagRows(planId, versionId, page, pageSize, searchQuery, filterGroup)
  const { data: dropdownData } = useDropdownLookups()
  const hierarchyData = useHierarchyForDag()
  const updatePlanHeader = useUpdatePlanHeader(planId)

  // Column order + saved views
  const { data: planColumnOrder } = useColumnOrder(planId)
  const updateColumnOrderMutation = useUpdateColumnOrder(planId)
  const { data: allViews } = useColumnViews()

  // Per-user column widths. Read through a ref when building columnDefs so a
  // saved width doesn't rebuild every column def; the effect below applies
  // widths that arrive after the grid has already created its columns.
  const { data: savedColumnWidths } = useColumnWidths(planId)
  const updateColumnWidthMutation = useUpdateColumnWidth(planId)
  const savedColumnWidthsRef = useRef(savedColumnWidths)
  savedColumnWidthsRef.current = savedColumnWidths
  const columnWidthsLoaded = savedColumnWidths !== undefined

  // Row order
  const reorderRowMutation = useReorderDagRow(planId, versionId)

  // SSE live updates
  useDagSSE(planId, versionId)
  useStuckComputingWatcher(planId, versionId)

  // Column visibility
  const effectiveHiddenColumnIds = useColumnVisibility(
    planId,
    versionId,
    primaryAttributes,
    metadata?.columns
  )

  // Frozen (pinned-left) columns — local working copy, decoupled from column
  // order (see useColumnFreezeStore). A saved view persists its own freeze on
  // the backend and re-seeds this store when selected.
  // Scoped to the active view so each view keeps its own frozen columns.
  // (activeView is resolved below; the hook call lives after it.)
  const gridContainerRef = useRef<HTMLDivElement>(null)

  // Selectable columns (dynamic, non-Identity, non-fixed) for column selector
  // const FIXED_IDS = useMemo(() => new Set(['description', 'plan_sku_num']), [])
  const selectableColumns = useMemo(
    () => (metadata?.columns ? getConfigurableColumns(metadata.columns) : []),
    [metadata]
  )
  const selectableGroups = useMemo(
    () =>
      metadata?.groups.filter(
        g => g !== 'Identity' && selectableColumns.some(c => c.group === g)
      ) ?? [],
    [metadata, selectableColumns]
  )

  // Views are user-scoped, not plan-scoped — only offer views whose columns
  // all exist in this plan's DAG column catalog.
  const selectableColumnIdSet = useMemo(
    () => new Set(selectableColumns.map(c => c.id)),
    [selectableColumns]
  )
  const applicableViews = useMemo(
    () =>
      (allViews ?? []).filter(v =>
        v.columns.every(id => selectableColumnIdSet.has(id))
      ),
    [allViews, selectableColumnIdSet]
  )
  const activeViewId = useActiveColumnViewStore(s => s.getActiveView(planId))
  const setActiveViewId = useActiveColumnViewStore(s => s.setActiveView)
  const activeView = useMemo(
    () => applicableViews.find(v => v.id === activeViewId) ?? null,
    [applicableViews, activeViewId]
  )
  const freezeScope = freezeScopeKey(activeView?.id)
  const pinnedColumnIds = usePinnedColumnIds(freezeScope)
  // A view's saved freeze (backend) seeds its local scope when the view is
  // selected or saved (`updated_at` changes). Edits made afterwards stay
  // session-only until "Save changes", like column order.
  const replaceFreezeScope = useColumnFreezeStore(s => s.replaceScope)
  const activeViewFrozenColumns = activeView?.frozen_columns
  useEffect(() => {
    if (!activeView) return
    replaceFreezeScope(
      freezeScopeKey(activeView.id),
      activeViewFrozenColumns ?? []
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeView?.id, activeView?.updated_at, replaceFreezeScope])
  // AgGridReact attaches its api after our effects first run, so pins must
  // be (re)applied once the grid reports ready.
  const [gridReady, setGridReady] = useState(false)

  // Selecting/leaving a view seeds the (global) column-visibility store with
  // exactly the view's columns, then lets the user keep adjusting from there
  // via the existing "Columns" popover — it does not alter the saved view.
  const hideAllColumns = useColumnVisibilityStore(s => s.hideAll)
  const hiddenColumnsStoreValue = useColumnVisibilityStore(s => s.hiddenColumns)
  const hiddenBeforeViewRef = useRef<string[] | null>(null)

  const handleSelectView = useCallback(
    (viewId: string | null) => {
      if (viewId) {
        const view = applicableViews.find(v => v.id === viewId)
        if (view) {
          if (hiddenBeforeViewRef.current === null) {
            hiddenBeforeViewRef.current = Array.from(hiddenColumnsStoreValue)
          }
          const viewColumnIds = new Set(view.columns)
          const toHide = selectableColumns
            .filter(c => !viewColumnIds.has(c.id))
            .map(c => c.id)
          hideAllColumns(toHide)
        }
      } else if (hiddenBeforeViewRef.current !== null) {
        hideAllColumns(hiddenBeforeViewRef.current)
        hiddenBeforeViewRef.current = null
      }
      setActiveViewId(planId, viewId)
    },
    [
      applicableViews,
      selectableColumns,
      hideAllColumns,
      hiddenColumnsStoreValue,
      setActiveViewId,
      planId,
    ]
  )

  // While a view is active it fully owns the visible column order (a
  // reusable, cross-category subset); otherwise the plan's own persisted
  // order applies.
  const effectiveColumnOrder = activeView ? activeView.columns : planColumnOrder

  // Filterable columns for the "Filters" rule builder (broader than
  // selectableColumns: description/plan_sku_num/Identity fields stay
  // filterable even though they're not configurable in the column selector).
  const filterableColumns = useMemo(
    () => metadata?.columns.filter(isFilterableColumn) ?? [],
    [metadata]
  )

  const rows: DagRowRead[] = useMemo(() => rowsData?.rows ?? [], [rowsData])

  // Store values (from patch-success full snapshot + SSE partial merges) win
  // over the rows-load snapshot, so in-flight updates are not clobbered when
  // the rows query refetches with stale cached data.
  const storeAggregates = useDagTableStore(s => s.aggregates)
  const columnTotals = useMemo<Record<string, ColumnTotal[]>>(() => {
    if (!metadata) return {}
    const merged = { ...(rowsData?.aggregates ?? {}), ...storeAggregates }
    const out: Record<string, ColumnTotal[]> = {}
    for (const col of metadata.columns) {
      if (col.kind !== 'aggregate') continue
      const anchor = col.deps?.[0]
      if (!anchor) continue
      const value = merged[col.id]
      if (value == null) continue
      ;(out[anchor] ??= []).push({
        id: col.id,
        label: col.label,
        format: col.format,
        value,
      })
    }
    return out
  }, [metadata, rowsData?.aggregates, storeAggregates])

  // Total values change on every aggregate SSE event. Headers read them live
  // from useColumnTotalsStore, so columnDefs (rebuilding every column is the
  // expensive part) only follows the *set* of columns that show totals.
  const setLiveColumnTotals = useColumnTotalsStore(s => s.setTotals)
  useLayoutEffect(() => {
    setLiveColumnTotals(columnTotals)
  }, [columnTotals, setLiveColumnTotals])
  useEffect(() => () => setLiveColumnTotals({}), [setLiveColumnTotals])
  const columnTotalsShape = Object.keys(columnTotals)
    .sort()
    .map(k => `${k}:${columnTotals[k].length}`)
    .join('|')
  const [columnTotalsForDefs, setColumnTotalsForDefs] = useState(columnTotals)
  const [prevColumnTotalsShape, setPrevColumnTotalsShape] =
    useState(columnTotalsShape)
  if (prevColumnTotalsShape !== columnTotalsShape) {
    setPrevColumnTotalsShape(columnTotalsShape)
    setColumnTotalsForDefs(columnTotals)
  }

  const totalCount = rowsData?.pagination.total ?? 0
  const totalPages = Math.ceil(totalCount / pageSize) || 1

  // Dropdown map: category -> options[]
  const dropdownMap = useMemo(
    () => dropdownData?.categories ?? {},
    [dropdownData]
  )

  // Column metadata map: column_id -> DagColumnMeta
  const columnsMap = useMemo(() => {
    if (!metadata) return new Map()
    return new Map(metadata.columns.map(c => [c.id, c]))
  }, [metadata])

  // Row version lookup for PATCH OCC
  const rowVersionMap = useMemo(() => {
    const map = new Map<string, number>()
    for (const r of rows) map.set(r.id, r.row_version)
    return map
  }, [rows])

  const getRowVersion = useCallback(
    (rowId: string) => rowVersionMap.get(rowId) ?? 0,
    [rowVersionMap]
  )

  // ---- State management ----------------------------------------------------
  const state = useProductsTableState(rows)

  // Compute dummy SKU line item IDs from selected rows
  const dummySkuLineItemIds = useMemo(() => {
    return Array.from(state.selectedItemIds).filter(id => {
      const row = rows.find(r => r.id === id)
      if (!row) return false
      return !row.cells['plan_sku_num'] || !!row.cells['is_new_sku']
    })
  }, [state.selectedItemIds, rows])

  const getBulkSelectedRows = useCallback(
    (rowId: string) => {
      const selectedIds = state.selectedItemIdsRef.current
      if (selectedIds.size <= 1 || !selectedIds.has(rowId)) return null

      const selectedRows = Array.from(
        state.selectedItemsCacheRef.current.values()
      ).filter(row => selectedIds.has(row.id))

      return selectedRows.length > 1 ? selectedRows : null
    },
    [state.selectedItemIdsRef, state.selectedItemsCacheRef]
  )

  // Column reordering: persist the plan's own order on drag, unless a view
  // is active (a view is a session-local lens and must not overwrite it).
  // `onDragStopped` is AG Grid's generic end-of-any-drag event (shared with
  // row dragging), so we use `onColumnMoved` instead and gate on
  // `event.source === 'uiColumnMoved'` — the only source a genuine column
  // drag ever uses. Our own programmatic `applyColumnState` reapply (below)
  // also emits `columnMoved` with `finished: true`, but always with
  // `source: 'api'`, so this check also prevents a reapply -> persist ->
  // reapply feedback loop.
  const handleColumnMoved = useCallback(
    (event: ColumnMovedEvent) => {
      if (!event.finished || event.source !== 'uiColumnMoved') return
      const gridApi = state.gridRef.current?.api
      if (!gridApi) return
      // Dragging a column across the freeze line makes AG Grid pin/unpin it
      // on its own — mirror that into the freeze store so the two never
      // diverge (and the divider follows).
      const dynamicStates = gridApi
        .getColumnState()
        .filter(s => !NON_REORDERABLE_COLUMN_IDS.has(s.colId))
      const { setPinned } = useColumnFreezeStore.getState()
      setPinned(
        freezeScope,
        dynamicStates.filter(s => s.pinned === 'left').map(s => s.colId),
        true
      )
      setPinned(
        freezeScope,
        dynamicStates.filter(s => s.pinned !== 'left').map(s => s.colId),
        false
      )
      if (activeViewId) return
      const orderedIds = gridApi
        .getColumnState()
        .map(s => s.colId)
        .filter(
          (id): id is string => !!id && !NON_REORDERABLE_COLUMN_IDS.has(id)
        )
      updateColumnOrderMutation.mutate(orderedIds)
    },
    [activeViewId, freezeScope, state.gridRef, updateColumnOrderMutation]
  )

  // Persist a column width once the user releases the resize handle. Gated on
  // `uiColumnResized` so our own programmatic `applyColumnState` (source
  // 'api') never feeds back into a save.
  const handleColumnResized = useCallback(
    (event: ColumnResizedEvent) => {
      if (!event.finished || event.source !== 'uiColumnResized') return
      for (const col of event.columns ?? []) {
        const width = Math.round(col.getActualWidth())
        if (
          width > 0 &&
          savedColumnWidthsRef.current?.[col.getColId()] !== width
        ) {
          updateColumnWidthMutation.mutate({
            columnKey: col.getColId(),
            width,
          })
        }
      }
    },
    [updateColumnWidthMutation]
  )

  // Snapshot of the currently visible columns, in their live grid order —
  // used as `columns` when saving/updating a view.
  const getCurrentColumnIds = useCallback(() => {
    const gridApi = state.gridRef.current?.api
    if (!gridApi) return []
    return gridApi
      .getColumnState()
      .filter(
        (s): s is typeof s & { colId: string } =>
          !s.hide && !!s.colId && !NON_REORDERABLE_COLUMN_IDS.has(s.colId)
      )
      .map(s => s.colId)
  }, [state.gridRef])

  // Tab/Enter navigation for DAG cells
  const { navigateTab, navigateEnter } = useDagTabNavigation(
    state.gridRef,
    columnsMap,
    isReadOnly,
    gridContainerRef
  )
  const clearFocus = useDagTableStore(s => s.clearFocus)
  const highlightedRowId = useDagTableStore(s => s.highlightedRowId)

  // Re-apply row classes when audit highlight changes
  useEffect(() => {
    state.gridRef.current?.api?.redrawRows()
  }, [highlightedRowId, state.gridRef])

  // Refresh already-rendered dropdown cells when the dropdown options
  // cache updates (e.g. after saving a new custom "Other..." value),
  // since AG Grid doesn't re-read cellRendererParams on its own.
  useEffect(() => {
    state.gridRef.current?.api?.refreshCells({ force: true })
  }, [dropdownMap, state.gridRef])

  // Click-away: clear focus when clicking outside the grid
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const gridEl = document.querySelector(
        '.ag-theme-michaels.plan-detail-grid'
      ) as HTMLElement | null
      if (gridEl && !gridEl.contains(e.target as Node)) {
        // Don't clear focus if clicking inside a portal dropdown
        const target = e.target as HTMLElement
        if (target.closest('.dag-dropdown-portal')) return
        clearFocus()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [clearFocus])

  // Mutations with callbacks
  const mutations = useProductsTableMutations(
    planId,
    {
      onEditSuccess: state.clearEditingCell,
      onRowsPatched: state.applyPatchedRowsToSelectionCache,
      onRowRefreshNeeded: (rowId: string) => {
        if (state.gridRef.current?.api) {
          const rowNode = state.gridRef.current.api.getRowNode(rowId)
          if (rowNode) {
            state.gridRef.current.api.refreshCells({
              rowNodes: [rowNode],
              force: true,
            })
          }
        }
      },
      onCopySuccess: state.closeCopyDialog,
      onDeleteSuccess: () => {
        if (state.dialogState.selectedItemId) {
          state.handleSelectItem(state.dialogState.selectedItemId, false)
        }
        state.closeDeleteDialog()
      },
    },
    {
      versionId,
      columnsMap,
      userId: 'current-user',
    }
  )

  useDagDropdownPaste({
    isReadOnly,
    columnsMap,
    rows,
    dropdownMap,
    hierarchyData,
    getRowVersion,
    handleCellEdit: mutations.handleCellEdit,
    handleMultiCellEdit: mutations.handleMultiCellEdit,
  })

  // ---- Drawer / modal states -----------------------------------------------
  const [assignStoreModelState, setAssignStoreModelState] = useState<{
    open: boolean
    lineIds: string[]
    existingStoreModelId: string | null
    startAtGroups: boolean
    initialStoreGroupIds?: string[]
  }>({
    open: false,
    lineIds: [],
    existingStoreModelId: null,
    startAtGroups: false,
  })

  const [planWavesState, setPlanWavesState] = useState<{
    open: boolean
    item: PlanDetailItem | null
  }>({ open: false, item: null })

  const [multilocState, setMultilocState] = useState<{
    modalOpen: boolean
    drawerOpen: boolean
    item: PlanDetailItem | null
  }>({ modalOpen: false, drawerOpen: false, item: null })

  const [columnsConfigOpen, setColumnsConfigOpen] = useState(false)

  const [secondaryDrawerState, setSecondaryDrawerState] = useState<{
    open: boolean
    rowId: string | null
  }>({ open: false, rowId: null })
  const [promotingPrimaryColumnId, setPromotingPrimaryColumnId] = useState<
    string | null
  >(null)

  const [assignParentState, setAssignParentState] = useState<{
    open: boolean
    rowIds: string[]
  }>({ open: false, rowIds: [] })

  const [piabState, setPiabState] = useState<{
    open: boolean
    rowId: string | null
  }>({ open: false, rowId: null })

  const [manageImagesState, setManageImagesState] = useState<{
    open: boolean
    lineId: string | null
  }>({ open: false, lineId: null })

  const [carouselState, setCarouselState] = useState<{
    open: boolean
    lineId: string | null
  }>({ open: false, lineId: null })

  const [retailPricingState, setRetailPricingState] = useState<{
    open: boolean
    item: PlanDetailItem | null
    bulkItems?: PlanDetailItem[]
  }>({ open: false, item: null })

  const [bulkColumnEditOpen, setBulkColumnEditOpen] = useState(false)

  const [forecastModelState, setForecastModelState] = useState<{
    open: boolean
    item: PlanDetailItem | null
  }>({ open: false, item: null })

  const [storeModelRequiredState, setStoreModelRequiredState] = useState<{
    open: boolean
    item: PlanDetailItem | null
  }>({ open: false, item: null })

  const [openCommentsRefId, setOpenCommentsRefId] = useState<string | null>(
    null
  )
  const handleOpenComments = useCallback((lineRefId: string) => {
    setOpenCommentsRefId(lineRefId)
  }, [])

  // Deep-link: ?openComments=<line_ref_id> jumps to the SKU's row (switching
  // page if needed), highlights it and opens the comments drawer.
  const [searchParams, setSearchParams] = useSearchParams()
  const setHighlightedRowId = useDagTableStore(s => s.setHighlightedRowId)
  const locatingRefIdRef = useRef<string | null>(null)
  const locatingToastIdRef = useRef<string | null>(null)
  const dismissLocatingToast = useCallback(() => {
    if (locatingToastIdRef.current) {
      toast.dismiss(locatingToastIdRef.current)
      locatingToastIdRef.current = null
    }
  }, [])
  useEffect(() => dismissLocatingToast, [dismissLocatingToast])
  useEffect(() => {
    const deepRefId = searchParams.get('openComments')
    if (!deepRefId) {
      locatingRefIdRef.current = null
      dismissLocatingToast()
      return
    }
    // Tell the user we're working on it — loading rows + paging can take a moment.
    if (!locatingToastIdRef.current) {
      locatingToastIdRef.current = toast.info(
        <span className="flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" />
          Locating comment…
        </span>,
        0
      )
    }
    if (isFetching || !versionId) return

    const removeDeepLinkParam = () =>
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev)
          next.delete('openComments')
          return next
        },
        { replace: true }
      )

    const match = rows.find(r => r.line_ref_id === deepRefId)
    if (match) {
      locatingRefIdRef.current = null
      setOpenCommentsRefId(deepRefId)
      setHighlightedRowId(match.id)
      dismissLocatingToast()
      // The grid is autoHeight (the page scrolls, not the grid), so scroll the
      // row element itself. Retry briefly: the row may not be rendered yet.
      let attempts = 0
      const scrollToRow = () => {
        const el = gridContainerRef.current?.querySelector(
          `.ag-row[row-id="${CSS.escape(match.id)}"]`
        )
        if (el) {
          el.scrollIntoView({ block: 'center', behavior: 'smooth' })
        } else if (attempts++ < 20) {
          requestAnimationFrame(scrollToRow)
        }
      }
      requestAnimationFrame(scrollToRow)
      removeDeepLinkParam()
      return
    }

    if (locatingRefIdRef.current === deepRefId) return
    locatingRefIdRef.current = deepRefId

    // The row may be hidden by the active search/filter — clear them first.
    if (searchQuery) setSearchQuery('')
    if (filterGroup) setFilterGroup(null)

    findDagRowIndexByLineRef(planId, versionId, deepRefId)
      .then(index => {
        if (index === null) {
          locatingRefIdRef.current = null
          dismissLocatingToast()
          toast.error('This SKU is no longer in the plan')
          setOpenCommentsRefId(deepRefId)
          removeDeepLinkParam()
          return
        }
        // The row shows up (and the effect re-runs) once this page loads.
        onPageChange(Math.floor(index / pageSize) + 1)
      })
      .catch((error: unknown) => {
        locatingRefIdRef.current = null
        dismissLocatingToast()
        toast.error(getErrorMessage(error))
        removeDeepLinkParam()
      })
  }, [
    rows,
    isFetching,
    searchParams,
    setSearchParams,
    versionId,
    planId,
    pageSize,
    searchQuery,
    filterGroup,
    onPageChange,
    setHighlightedRowId,
    dismissLocatingToast,
  ])

  const commentsDrawerRow = useMemo(() => {
    if (!openCommentsRefId) return null
    return rows.find(r => r.line_ref_id === openCommentsRefId) ?? null
  }, [rows, openCommentsRefId])

  const handleAssignStoreGroup = useCallback(
    (itemId: string) => {
      const row = rows.find(r => r.id === itemId)
      const existingId = (row?.cells['store_model_id'] as string) ?? null
      const activeGroupIds =
        (row?.cells['active_store_group_ids'] as string[]) ?? undefined
      setAssignStoreModelState({
        open: true,
        lineIds: [itemId],
        existingStoreModelId: existingId,
        startAtGroups: false,
        initialStoreGroupIds: activeGroupIds,
      })
    },
    [rows]
  )

  const handlePlanWaves = useCallback(
    (itemId: string) => {
      const row = rows.find(r => r.id === itemId)
      const item = row ? rowDataToPlanDetailItem(dagRowToRowData(row)) : null
      setPlanWavesState({ open: true, item })
    },
    [rows]
  )

  const handleMultiloc = useCallback((item: PlanDetailItem) => {
    setMultilocState({ modalOpen: false, drawerOpen: true, item })
  }, [])

  const handleBulkAssignStoreGroup = useCallback(() => {
    const ids = Array.from(state.selectedItemIds)
    const storeModelIds = ids.map(id => {
      const row = rows.find(r => r.id === id)
      return (row?.cells['store_model_id'] as string) ?? null
    })
    const allSame = storeModelIds.every(id => id && id === storeModelIds[0])
    setAssignStoreModelState({
      open: true,
      lineIds: ids,
      existingStoreModelId: allSame ? storeModelIds[0] : null,
      startAtGroups: false,
    })
  }, [state.selectedItemIds, rows])

  const handleBulkManageStoreGroups = useCallback(() => {
    const ids = Array.from(state.selectedItemIds)
    const storeModelIds = ids.map(id => {
      const row = rows.find(r => r.id === id)
      return (row?.cells['store_model_id'] as string) ?? null
    })
    const sharedModelId = storeModelIds[0]
    setAssignStoreModelState({
      open: true,
      lineIds: ids,
      existingStoreModelId: sharedModelId,
      startAtGroups: true,
    })
  }, [state.selectedItemIds, rows])

  const canManageStoreGroups = useMemo(() => {
    if (state.selectedItemIds.size === 0) return false
    const ids = Array.from(state.selectedItemIds)
    const storeModelIds = ids.map(id => {
      const row = rows.find(r => r.id === id)
      return (row?.cells['store_model_id'] as string) ?? null
    })
    return storeModelIds.every(id => id != null && id === storeModelIds[0])
  }, [state.selectedItemIds, rows])

  const handleViewSecondaryAttributes = useCallback((itemId: string) => {
    setSecondaryDrawerState({ open: true, rowId: itemId })
  }, [])

  const handleAssignParentSku = useCallback((itemId: string) => {
    setAssignParentState({ open: true, rowIds: [itemId] })
  }, [])

  // Only Child-level SKUs can be assigned a parent; a bulk selection may
  // also contain Parent/standalone rows, which are simply ignored here.
  const childSelectedRowIds = useMemo(() => {
    return Array.from(state.selectedItemIds).filter(id => {
      const row = rows.find(r => r.id === id)
      return row?.cells['sku_level'] === 'Child'
    })
  }, [state.selectedItemIds, rows])

  const handleBulkAssignParentSku = useCallback(() => {
    if (childSelectedRowIds.length === 0) return
    setAssignParentState({ open: true, rowIds: childSelectedRowIds })
  }, [childSelectedRowIds])

  const assignParentChildItems = useMemo(() => {
    return assignParentState.rowIds
      .map(id => rows.find(r => r.id === id))
      .filter((r): r is DagRowRead => !!r)
      .map(row => ({
        rowId: row.id,
        skuNumber: (row.cells['plan_sku_num'] as string) ?? null,
        description: (row.cells['description'] as string) ?? null,
        existingParent: row.parent_line_item ?? null,
        unitQty: row.cells['unit_qty_per_piab'] ?? null,
      }))
  }, [assignParentState.rowIds, rows])

  const handleManagePIAB = useCallback((itemId: string) => {
    setPiabState({ open: true, rowId: itemId })
  }, [])

  const handleManageImages = useCallback((itemId: string) => {
    setManageImagesState({ open: true, lineId: itemId })
  }, [])

  const handleOpenCarousel = useCallback((lineId: string) => {
    setCarouselState({ open: true, lineId })
  }, [])

  const handleCreatePricing = useCallback(
    (itemId: string) => {
      const row = rows.find(r => r.id === itemId)
      const item = row ? rowDataToPlanDetailItem(dagRowToRowData(row)) : null
      setRetailPricingState({ open: true, item })
    },
    [rows]
  )

  const handleBulkApplyPricing = useCallback(() => {
    if (state.selectedItemsData.length === 0) return
    setRetailPricingState({
      open: true,
      item: null,
      bulkItems: state.selectedItemsData,
    })
  }, [state.selectedItemsData])

  const handleOpenBulkEditColumn = useCallback(() => {
    setBulkColumnEditOpen(true)
  }, [])

  const handleCreateForecast = useCallback(
    (itemId: string) => {
      const row = rows.find(r => r.id === itemId)
      const item = row ? rowDataToPlanDetailItem(dagRowToRowData(row)) : null
      if (!item?.storeModelId) {
        setStoreModelRequiredState({ open: true, item })
      } else {
        setForecastModelState({ open: true, item })
      }
    },
    [rows]
  )

  const handleGoToStoreModel = useCallback(() => {
    const itemId = storeModelRequiredState.item?.id
    if (!itemId) return
    setStoreModelRequiredState({ open: false, item: null })
    setAssignStoreModelState({
      open: true,
      lineIds: [itemId],
      existingStoreModelId: null,
      startAtGroups: false,
    })
  }, [storeModelRequiredState.item])

  const secondaryDrawerRow = useMemo(() => {
    if (!secondaryDrawerState.rowId) return null
    return rows.find(row => row.id === secondaryDrawerState.rowId) ?? null
  }, [rows, secondaryDrawerState.rowId])

  const handlePromoteToPrimary = useCallback(
    (columnId: string) => {
      if (primaryAttributes.includes(columnId)) return

      const nextPrimaryAttributes = Array.from(
        new Set([...primaryAttributes, columnId])
      )

      setPromotingPrimaryColumnId(columnId)
      updatePlanHeader.mutate(
        { primary_attribute_ids: nextPrimaryAttributes },
        {
          onError: () => {
            toast.error('Failed to promote attribute to primary')
          },
          onSettled: () => {
            setPromotingPrimaryColumnId(null)
          },
        }
      )
    },
    [primaryAttributes, updatePlanHeader]
  )

  // ---- Build row data with option rows ------------------------------------
  // Reuse the RowData object while its DagRowRead is unchanged (cache updates
  // keep untouched rows by reference), so AG Grid only refreshes rows that
  // actually changed instead of every row on every SSE event.
  const rowDataCacheRef = useRef(new WeakMap<DagRowRead, RowData>())
  const rowData: RowData[] = useMemo(() => {
    const cache = rowDataCacheRef.current
    const result: RowData[] = []
    for (const row of rows) {
      let converted = cache.get(row)
      if (!converted) {
        converted = dagRowToRowData(row)
        cache.set(row, converted)
      }
      result.push(converted)

      if (state.expandedItems.has(row.id)) {
        const options = MOCK_OPTIONS.default
        for (const option of options) {
          result.push({
            id: `${row.id}-${option.id}`,
            isOptionRow: true,
            option,
            parentId: row.id,
          })
        }
      }
    }
    return result
  }, [rows, state.expandedItems])

  // ---- Row reordering -------------------------------------------------------
  // A client-side column sort reorders what's displayed without touching
  // `rows`/sort_key order, which would break the cluster-contiguity
  // assumptions below — disable row drag while a sort is active.
  const hasActiveSortRef = useRef(false)

  const handleSortChanged = useCallback(() => {
    const gridApi = state.gridRef.current?.api
    hasActiveSortRef.current =
      gridApi?.getColumnState().some(s => !!s.sort) ?? false
  }, [state.gridRef])

  // Blocks a new drag while a previous row-reorder call is still in flight —
  // starting a second drag before the first's cache/row_version update has
  // propagated back would send a stale If-Match and 409.
  const isRowReorderPendingRef = useRef(false)

  const handleRowDragEnd = useCallback(
    (event: RowDragEndEvent<RowData>) => {
      const gridApi = state.gridRef.current?.api
      if (!gridApi) return

      const revert = () => gridApi.setGridOption('rowData', rowData)

      if (isReadOnly || hasActiveSortRef.current) {
        revert()
        return
      }
      if (isRowReorderPendingRef.current) {
        revert()
        return
      }
      const movedData = event.node.data
      if (!movedData || movedData.isOptionRow) {
        revert()
        return
      }

      const rawCandidateIds: string[] = []
      gridApi.forEachNode(node => {
        if (node.data && !node.data.isOptionRow) {
          rawCandidateIds.push(node.data.id)
        }
      })

      const plan = computeReorderPlan(rows, rawCandidateIds, movedData.id)
      if (!plan) {
        revert()
        return
      }

      const queryKey = getDagRowsQueryKey(
        planId,
        versionId!,
        page,
        pageSize,
        searchQuery,
        filterGroup
      )
      const previousSnapshot =
        queryClient.getQueryData<DagTableLoadResponse>(queryKey)
      const rowById = new Map(rows.map(r => [r.id, r]))
      queryClient.setQueryData<DagTableLoadResponse>(queryKey, old => {
        if (!old) return old
        return {
          ...old,
          rows: plan.newOrder
            .map(id => rowById.get(id))
            .filter((r): r is DagRowRead => !!r),
        }
      })

      isRowReorderPendingRef.current = true
      void (async () => {
        try {
          for (const { rowId, anchor } of plan.anchors) {
            await reorderRowMutation.mutateAsync({
              lineId: rowId,
              rowVersion: getRowVersion(rowId),
              anchor,
            })
          }
          // Don't trust the reorder response's `row_version` for the cache
          // bump — the backend's reorder endpoint currently echoes a stale
          // value there. Refetch from `/table/rows` (the source `rows`/
          // `getRowVersion` are built from) so subsequent edits use the
          // real, authoritative version instead of a wrong cached one.
          await queryClient.invalidateQueries({
            queryKey: queryKeys.dagTable.rowsPrefix(planId, versionId!),
          })
        } catch {
          queryClient.setQueryData(queryKey, previousSnapshot)
          queryClient.invalidateQueries({
            queryKey: queryKeys.dagTable.rowsPrefix(planId, versionId!),
          })
        } finally {
          isRowReorderPendingRef.current = false
        }
      })()
    },
    [
      isReadOnly,
      rows,
      rowData,
      state.gridRef,
      planId,
      versionId,
      page,
      pageSize,
      searchQuery,
      filterGroup,
      queryClient,
      reorderRowMutation,
      getRowVersion,
    ]
  )

  // ---- Column definitions --------------------------------------------------

  // Renderer handlers with stable identity. Most of these close over `rows`
  // (and the selection), so they changed on every row update; as renderer
  // deps that rebuilt every columnDef and remounted every name/actions cell
  // on each SSE update and each checkbox click.
  const onSelectAll = useStableCallback(state.handleSelectAll)
  const onSelectItem = useStableCallback(state.handleSelectItem)
  const onDescriptionSave = useStableCallback(mutations.handleDescriptionSave)
  const onBulkCellEdit = useStableCallback(mutations.handleBulkCellEdit)
  const onSkuSave = useStableCallback(mutations.handleSkuSave)
  const getRowVersionStable = useStableCallback(getRowVersion)
  const onOpenCarousel = useStableCallback(handleOpenCarousel)
  const onCopyLine = useStableCallback(state.openCopyDialog)
  const onDeleteLine = useStableCallback(state.openDeleteDialog)
  const onAutoSaveBeforeMenu = useStableCallback(state.handleAutoSaveBeforeMenu)
  const onAssignStoreGroup = useStableCallback(handleAssignStoreGroup)
  const onMultiloc = useStableCallback(handleMultiloc)
  const onViewSecondaryAttributes = useStableCallback(
    handleViewSecondaryAttributes
  )
  const onPlanWaves = useStableCallback(handlePlanWaves)
  const onAssignParentSku = useStableCallback(handleAssignParentSku)
  const onManagePIAB = useStableCallback(handleManagePIAB)
  const onCreatePricing = useStableCallback(handleCreatePricing)
  const onCreateForecast = useStableCallback(handleCreateForecast)
  const onManageImages = useStableCallback(handleManageImages)
  const onOpenComments = useStableCallback(handleOpenComments)
  const onOpenBulkEditColumn = useStableCallback(handleOpenBulkEditColumn)

  // Header component with checkbox
  const NameHeaderComponent = useCallback(
    (props: DagColumnHeaderLabelProps) => {
      return (
        <div className="flex items-center gap-2.5 pl-4">
          <div className="w-6" />
          <Checkbox
            checked={state.allSelectedRef.current}
            onCheckedChange={onSelectAll}
            className="cursor-pointer"
            {...(state.someSelectedRef.current
              ? { 'data-state': 'indeterminate' }
              : {})}
          />
          <DagColumnHeaderLabel {...props} />
        </div>
      )
    },
    [onSelectAll, state.allSelectedRef, state.someSelectedRef]
  )

  // Name cell renderer
  const NameCellRenderer = useCallback(
    (props: { data?: RowData }) => {
      const row = props.data
      if (!row) return null

      if (row.isOptionRow && row.option) {
        return <OptionRowCell option={row.option} />
      }

      return (
        <NameCell
          {...(props as Parameters<typeof NameCell>[0])}
          data={row}
          isSelected={state.selectedItemIdsRef.current.has(row.id)}
          onToggleSelect={checked => onSelectItem(row.id, checked)}
          onSaveEdit={(id, _field, value) => {
            const selectedRows = getBulkSelectedRows(id)
            if (selectedRows) {
              onBulkCellEdit(selectedRows, 'description', value)
              return
            }

            onDescriptionSave(id, value, getRowVersionStable(id))
          }}
          navigateTab={navigateTab}
          navigateEnter={navigateEnter}
          isReadOnly={isReadOnly}
          onOpenCarousel={onOpenCarousel}
        />
      )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      onSelectItem,
      onDescriptionSave,
      onBulkCellEdit,
      getBulkSelectedRows,
      getRowVersionStable,
      navigateTab,
      navigateEnter,
      isReadOnly,
      onOpenCarousel,
    ]
  )

  // SKU cell renderer
  const SkuCellRenderer = useCallback(
    (props: { data?: RowData }) => {
      if (!props.data || props.data.isOptionRow) return null
      return (
        <SkuCell
          {...(props as Parameters<typeof SkuCell>[0])}
          onSkuSave={onSkuSave}
          isReadOnly={isReadOnly}
        />
      )
    },
    [onSkuSave, isReadOnly]
  )

  // Actions cell renderer
  const ActionsCellRenderer = useCallback(
    (props: { data?: RowData }) => {
      const row = props.data
      if (!row || row.isOptionRow) return null
      return (
        <LineActionsCell
          {...(props as Parameters<typeof LineActionsCell>[0])}
          data={row}
          onAssignStoreGroup={onAssignStoreGroup}
          onMultiloc={onMultiloc}
          onViewSecondaryAttributes={onViewSecondaryAttributes}
          onPlanWaves={onPlanWaves}
          onCopyLine={onCopyLine}
          onDeleteLine={onDeleteLine}
          triggerAutoSave={onAutoSaveBeforeMenu}
          isReadOnly={isReadOnly}
          onAssignParentSku={onAssignParentSku}
          onManagePIAB={onManagePIAB}
          onCreatePricing={onCreatePricing}
          onCreateForecast={onCreateForecast}
          onManageImages={onManageImages}
          onOpenComments={onOpenComments}
        />
      )
    },
    [
      onCopyLine,
      onDeleteLine,
      onAutoSaveBeforeMenu,
      onAssignStoreGroup,
      onMultiloc,
      onViewSecondaryAttributes,
      onPlanWaves,
      isReadOnly,
      onAssignParentSku,
      onManagePIAB,
      onCreatePricing,
      onCreateForecast,
      onManageImages,
      onOpenComments,
    ]
  )

  // Build column defs from DAG metadata
  const columnDefs = useMemo(() => {
    if (!metadata) return []
    return buildDagColumnDefs({
      columns: metadata.columns,
      groups: metadata.groups,
      columnOrder: effectiveColumnOrder,
      columnWidths: columnWidthsLoaded
        ? savedColumnWidthsRef.current
        : undefined,
      dropdownMap,
      hiddenColumnIds: effectiveHiddenColumnIds,
      isReadOnly,
      columnTotals: columnTotalsForDefs,
      onOpenBulkEditColumn: onOpenBulkEditColumn,
      NameHeaderComponent,
      NameCellRenderer,
      SkuCellRenderer,
      ActionsCellRenderer,
      DagCellRendererComponent: DagCellRenderer,
    })
  }, [
    metadata,
    effectiveColumnOrder,
    columnWidthsLoaded,
    dropdownMap,
    effectiveHiddenColumnIds,
    isReadOnly,
    columnTotalsForDefs,
    onOpenBulkEditColumn,
    NameHeaderComponent,
    NameCellRenderer,
    SkuCellRenderer,
    ActionsCellRenderer,
  ])

  // `maintainColumnOrder` keeps the grid's current column order across
  // columnDefs updates, so when the *set* of visible columns doesn't change
  // (e.g. switching between two saved views with the same columns in a
  // different order) the grid never picks up the new order on its own.
  // Explicitly re-apply it whenever the canonical order itself changes.
  //
  // Deliberately keyed on `effectiveColumnOrder`, not `columnDefs` — the
  // latter also changes for reasons unrelated to order (e.g. `columnTotals`
  // updating after a row edit/reorder refetches aggregates), and reapplying
  // in those cases would snap columns back to the plan's/view's *saved*
  // order, discarding a live column drag the user made while a view is
  // active (that drag is intentionally never persisted — see
  // `handleColumnMoved` — so it only lives in the grid's own state).
  useEffect(() => {
    const gridApi = state.gridRef.current?.api
    if (!gridApi) return
    const orderedIds = flattenColumnIds(columnDefs)
    gridApi.applyColumnState({
      state: orderedIds.map(colId => ({ colId })),
      applyOrder: true,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveColumnOrder, state.gridRef])

  // Reapply the user's frozen-column preference whenever it changes. Kept
  // imperative (rather than threaded into buildDagColumnDefs) so toggling a
  // pin doesn't rebuild columnDefs — mirrors the order-reapply effect above.
  useEffect(() => {
    const gridApi = state.gridRef.current?.api
    if (!gridApi || !metadata) return
    const dynamicIds = metadata.columns
      .map(c => c.id)
      .filter(
        id =>
          !NON_REORDERABLE_COLUMN_IDS.has(id) &&
          !effectiveHiddenColumnIds.has(id)
      )
    gridApi.applyColumnState({
      state: dynamicIds.map(colId => ({
        colId,
        pinned: pinnedColumnIds.has(colId) ? 'left' : null,
      })),
    })
  }, [
    pinnedColumnIds,
    metadata,
    effectiveHiddenColumnIds,
    state.gridRef,
    gridReady,
  ])

  // Apply saved widths that arrive after the grid created its columns
  // (`initialWidth` is ignored for existing columns).
  useEffect(() => {
    const gridApi = state.gridRef.current?.api
    if (!gridApi || !savedColumnWidths) return
    const entries = Object.entries(savedColumnWidths)
    if (entries.length === 0) return
    gridApi.applyColumnState({
      state: entries.map(([colId, width]) => ({ colId, width })),
    })
  }, [savedColumnWidths, state.gridRef])

  // Memoize AG Grid context to avoid unnecessary re-renders of all cells
  const gridContext = useMemo(
    () => ({
      onCellEdit: mutations.handleCellEdit,
      onMultiCellEdit: mutations.handleMultiCellEdit,
      onBulkCellEdit: mutations.handleBulkCellEdit,
      onBulkMultiCellEdit: mutations.handleBulkMultiCellEdit,
      getBulkSelectedRows,
      getRowVersion: getRowVersionStable,
      navigateTab,
      navigateEnter,
      isReadOnly: isReadOnly ?? false,
      dropdownMap,
      hierarchyData,
      planId,
      versionId,
    }),
    [
      mutations.handleCellEdit,
      mutations.handleMultiCellEdit,
      mutations.handleBulkCellEdit,
      mutations.handleBulkMultiCellEdit,
      getBulkSelectedRows,
      getRowVersionStable,
      navigateTab,
      navigateEnter,
      isReadOnly,
      dropdownMap,
      hierarchyData,
      planId,
      versionId,
    ]
  )

  // ---- Render --------------------------------------------------------------
  if (isLoading && rows.length === 0) {
    return (
      <div className="rounded-lg border border-border bg-card">
        <div className="flex items-center justify-center p-12 text-muted-foreground">
          Loading...
        </div>
      </div>
    )
  }

  return (
    <div
      style={
        {
          '--sticky-toolbar-offset': `${toolbarHeight}px`,
        } as React.CSSProperties
      }
    >
      {isReadOnly && (
        <div className="flex items-center gap-2 px-4 py-2 mb-8 bg-amber-50 border border-amber-200 rounded-md text-amber-800 text-sm">
          <Lock className="h-4 w-4 shrink-0" />
          <span>
            {planStatus === 'APPROVED'
              ? 'This version is locked because the plan has been approved.'
              : 'This version is locked because the plan is pending approval.'}
          </span>
        </div>
      )}

      {/* -mt-5/pt-5 + pb-5: 20px of air above and below the toolbar when stuck */}
      <div
        ref={toolbarRef}
        className="sticky top-0 z-20 -mt-5 bg-background pt-5 pb-5"
      >
        <ProductsTableToolbar
          onSearch={handleSearch}
          onAddItem={onAddItem}
          onImport={onImport}
          onExport={onExport}
          isExporting={isExporting}
          columns={selectableColumns}
          groups={selectableGroups}
          isReadOnly={isReadOnly}
          onOpenColumnsConfig={() => setColumnsConfigOpen(true)}
          filterableColumns={filterableColumns}
          dropdownMap={dropdownMap}
          filterGroup={filterGroup}
          onApplyFilter={handleApplyFilter}
          isFilterFetching={isFetching}
          activeViewId={activeView?.id ?? null}
          onSelectView={handleSelectView}
          getCurrentColumnIds={getCurrentColumnIds}
        />
      </div>

      {state.selectedItemIds.size > 0 && (
        <BulkActionsBar
          planId={planId}
          versionId={versionId}
          selectedCount={state.selectedItemIds.size}
          selectedItems={state.selectedItemsData}
          onClearSelection={state.handleClearSelection}
          onDeleteSuccess={onBulkDeleteSuccess}
          onAssignStoreGroup={handleBulkAssignStoreGroup}
          onManageStoreGroups={handleBulkManageStoreGroups}
          onApplyPricingModel={handleBulkApplyPricing}
          canManageStoreGroups={canManageStoreGroups}
          isReadOnly={isReadOnly}
          onReplaceDummySkus={() => onReplaceDummySkus?.(dummySkuLineItemIds)}
          hasDummySkusSelected={dummySkuLineItemIds.length > 0}
          onAssignParentSku={handleBulkAssignParentSku}
          hasChildSkusSelected={childSelectedRowIds.length > 0}
        />
      )}

      <div className="w-full">
        <div className="border border-[#e7e7e7] bg-white rounded-md">
          <div
            ref={gridContainerRef}
            className="ag-theme-michaels plan-detail-grid w-full relative"
          >
            <FreezeColumnDivider
              gridRef={state.gridRef}
              containerRef={gridContainerRef}
              freezeScope={freezeScope}
            />
            <AgGridReact<RowData>
              ref={state.gridRef}
              onGridReady={() => setGridReady(true)}
              rowData={rowData}
              columnDefs={columnDefs}
              theme="legacy"
              loading={isLoading}
              domLayout="autoHeight"
              rowHeight={68}
              headerHeight={49}
              groupHeaderHeight={49}
              overlayLoadingTemplate="<span>Loading...</span>"
              overlayNoRowsTemplate="<span>No items found</span>"
              suppressCellFocus={true}
              suppressRowHoverHighlight={false}
              suppressColumnVirtualisation={true}
              suppressMovableColumns={false}
              maintainColumnOrder={true}
              onColumnMoved={handleColumnMoved}
              onColumnResized={handleColumnResized}
              rowDragManaged={true}
              animateRows={true}
              onSortChanged={handleSortChanged}
              onRowDragEnd={handleRowDragEnd}
              context={gridContext}
              getRowId={params => params.data.id}
              getRowClass={params => {
                const classes: string[] = []
                if (params.data?.isOptionRow) {
                  classes.push('ag-row-option')
                }
                if (
                  params.data?.id &&
                  state.expandedItems.has(params.data.id)
                ) {
                  classes.push('ag-row-expanded')
                }
                if (highlightedRowId && params.data?.id === highlightedRowId) {
                  classes.push('ag-row-audit-highlight')
                }
                return classes.length ? classes.join(' ') : undefined
              }}
            />
          </div>

          <ProductsTablePagination
            page={page}
            pageSize={pageSize}
            totalPages={totalPages}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            pageSizeOptions={[20, 50, 100, 200]}
          />
        </div>
      </div>

      <ProductsTableDialogs
        copyDialogOpen={state.dialogState.copyOpen}
        onCopyDialogChange={open => {
          if (!open) state.closeCopyDialog()
        }}
        onConfirmCopy={() => {
          if (state.dialogState.selectedItemId) {
            mutations.handleConfirmCopy(state.dialogState.selectedItemId)
          }
        }}
        copyIsLoading={mutations.copyItemMutation.isPending}
        deleteDialogOpen={state.dialogState.deleteOpen}
        onDeleteDialogChange={open => {
          if (!open) state.closeDeleteDialog()
        }}
        onConfirmDelete={() => {
          if (state.dialogState.selectedItemId) {
            mutations.handleConfirmDelete(state.dialogState.selectedItemId)
          }
        }}
        deleteIsLoading={mutations.deleteItemMutation.isPending}
      />

      {versionId && (
        <AssignStoreModelDrawer
          open={assignStoreModelState.open}
          onOpenChange={open =>
            setAssignStoreModelState(prev => ({ ...prev, open }))
          }
          planId={planId}
          versionId={versionId}
          lineIds={assignStoreModelState.lineIds}
          existingStoreModelId={assignStoreModelState.existingStoreModelId}
          startAtGroups={assignStoreModelState.startAtGroups}
          initialStoreGroupIds={assignStoreModelState.initialStoreGroupIds}
          onSuccess={state.handleClearSelection}
        />
      )}

      {versionId && (
        <PlanWavesDrawer
          open={planWavesState.open}
          onOpenChange={open => setPlanWavesState(prev => ({ ...prev, open }))}
          item={planWavesState.item}
          planId={planId}
          versionId={versionId}
        />
      )}

      {versionId && (
        <RetailPricingDrawer
          open={retailPricingState.open}
          onOpenChange={open =>
            setRetailPricingState(prev => ({ ...prev, open }))
          }
          item={retailPricingState.item}
          bulkItems={retailPricingState.bulkItems}
          planId={planId}
          versionId={versionId}
        />
      )}

      <BulkColumnEditModal
        open={bulkColumnEditOpen}
        onOpenChange={setBulkColumnEditOpen}
        columnMeta={columnsMap.get(BULK_EDIT_COLUMN_ID) ?? null}
        onSubmit={mutations.handleBulkColumnEdit}
        isSubmitting={mutations.bulkColumnEditMutation.isPending}
      />

      {versionId && (
        <ForecastModelDrawer
          open={forecastModelState.open}
          onOpenChange={open =>
            setForecastModelState(prev => ({ ...prev, open }))
          }
          item={forecastModelState.item}
          planId={planId}
          versionId={versionId}
        />
      )}

      <Dialog
        open={storeModelRequiredState.open}
        onOpenChange={open =>
          setStoreModelRequiredState(prev => ({ ...prev, open }))
        }
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Store Model Required</DialogTitle>
            <DialogDescription>
              You must define a Store Model before creating a Forecast Model.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() =>
                setStoreModelRequiredState({ open: false, item: null })
              }
            >
              Cancel
            </Button>
            <Button
              className="cursor-pointer bg-[#CF1F2E] text-white hover:bg-[#CF1F2E]/90"
              onClick={handleGoToStoreModel}
            >
              Go to Store Model
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {versionId && (
        <MultilocDrawer
          open={multilocState.drawerOpen}
          onOpenChange={open =>
            setMultilocState(prev => ({ ...prev, drawerOpen: open }))
          }
          item={multilocState.item}
          planId={planId}
          versionId={versionId}
        />
      )}

      <SecondaryAttributesDrawer
        open={secondaryDrawerState.open}
        onOpenChange={open =>
          setSecondaryDrawerState(prev => ({ ...prev, open }))
        }
        row={secondaryDrawerRow}
        columns={metadata?.columns ?? []}
        primaryAttributes={primaryAttributes}
        dropdownMap={dropdownMap}
        onCellEdit={mutations.handleCellEdit}
        onPromoteToPrimary={handlePromoteToPrimary}
        promotingColumnId={promotingPrimaryColumnId}
        isReadOnly={isReadOnly}
      />

      <ColumnsConfigurationDrawer
        open={columnsConfigOpen}
        onOpenChange={setColumnsConfigOpen}
        columns={metadata?.columns ?? []}
        primaryAttributes={primaryAttributes}
        planId={planId}
      />

      {versionId && assignParentChildItems.length > 0 && (
        <AssignParentSkuDrawer
          open={assignParentState.open}
          onOpenChange={open =>
            setAssignParentState(prev => ({ ...prev, open }))
          }
          planId={planId}
          versionId={versionId}
          childItems={assignParentChildItems}
          onSuccess={
            assignParentState.rowIds.length > 1
              ? state.handleClearSelection
              : undefined
          }
        />
      )}

      {versionId &&
        piabState.rowId &&
        (() => {
          const row = rows.find(r => r.id === piabState.rowId)
          return (
            <ManagePIABDrawer
              open={piabState.open}
              onOpenChange={open => setPiabState(prev => ({ ...prev, open }))}
              planId={planId}
              versionId={versionId}
              parentRowId={piabState.rowId}
              parentSkuNumber={(row?.cells['plan_sku_num'] as string) ?? null}
              parentDescription={(row?.cells['description'] as string) ?? null}
            />
          )
        })()}

      <ThumbnailTracker />

      {versionId && manageImagesState.lineId && (
        <ManageLineImagesModal
          open={manageImagesState.open}
          onOpenChange={open =>
            setManageImagesState(prev => ({ ...prev, open }))
          }
          planId={planId}
          versionId={versionId}
          lineId={manageImagesState.lineId}
        />
      )}

      <LineItemCommentsDrawer
        open={!!openCommentsRefId}
        onOpenChange={open => {
          if (!open) setOpenCommentsRefId(null)
        }}
        lineRefId={openCommentsRefId}
        planId={planId}
        row={commentsDrawerRow}
      />

      {versionId && carouselState.lineId && (
        <ImageCarouselModal
          open={carouselState.open}
          onOpenChange={open => setCarouselState(prev => ({ ...prev, open }))}
          planId={planId}
          versionId={versionId}
          lineId={carouselState.lineId}
        />
      )}
    </div>
  )
}
